<template>
  <div class="container d-flex justify-content-center align-items-center min-vh-100">
    <div class="card shadow p-4 text-center" style="max-width: 400px; width: 100%;">
      <h3 class="mb-4 fw-bold text-dark">NTN Portal Access</h3>
      <p class="text-muted mb-4 small">
        Sign in using your preferred identity provider.
      </p>

      <!-- Google Button (Compliant with Google Brand Guidelines: White background, precise shadows, text) -->
      <button 
        id="googleBtn"
        @click="handleSignIn('google')" 
        class="btn btn-white border shadow-sm w-100 py-2 mb-3 d-flex align-items-center justify-content-center btn-brand"
      >
        <!-- Official Google 4-Color SVG Icon -->
        <svg class="me-2" xmlns="http://w3.org" width="18" height="18" viewBox="0 0 48 48">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.5 24c0-1.55-.15-3.24-.47-4.77H24v9.03h12.75c-.55 2.97-2.22 5.5-4.73 7.18l7.35 5.69C43.69 37.07 46.5 31.12 46.5 24z"/>
          <path fill="#FBBC05" d="M10.54 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.98-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.35-5.69c-2.03 1.36-4.64 2.19-8.54 2.19-6.26 0-11.57-4.22-13.46-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
        <span class="fw-semibold text-secondary">Sign in with Google</span>
      </button>

      <!-- GitHub Button (Compliant with GitHub Brand Guidelines: Dark background, exact color #24292e) -->
      <button 
        id="githubBtn"
        @click="handleSignIn('github')" 
        class="btn btn-dark w-100 py-2 d-flex align-items-center justify-content-center btn-brand btn-github"
      >
        <!-- Official GitHub White Octocat SVG Icon -->
        <svg class="me-2" xmlns="http://w3.org" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27s1.36.09 2 .27c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8"/>
        </svg>
        <span class="fw-semibold">Sign in with GitHub</span>
      </button>

      <div class="row mt-4">
        <p class="text-muted mb-1 small">
          Not registered yet?
        </p>
        <button type="button" class="btn btn-success" @click="registrationModal=true">
          Click to Register
        </button>
      </div>

    </div>
    <RegistrationFormModal 
      v-if="registrationModal" 
      @formDataSubmitted="submitRegistration"
      @formClosed="closedModal"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider, githubProvider } from '@/utils/firebase';
import { isFirebase, defaultPage, ApiEndpoint } from '@/utils/config';
import { GOOGLE_CLIENT_ID, GITHUB_CLIENT_ID } from '@/utils/config';
import { useAuthStore } from '@/stores/authStore';
import { setPKCE } from '@/utils/pkce';

import RegistrationFormModal from '@/components/RegistrationFormModal.vue';

const router = useRouter();
const authStore = useAuthStore();

const registrationModal = ref(false);

const handleSignIn = async (platform) => {
  const provider = platform === 'google' ? googleProvider : githubProvider;
  try {
    if (isFirebase) {
      // Trigger the secure native browser window popup handler
      const result = await signInWithPopup(auth, provider);
      // Immediately kick off backend validation endpoint
      await authStore.checkSession(result.user);
      // Handshake successful! Route to the primary portal index
      router.push({ name: defaultPage });
    } else if (provider === 'github') {
      await loginWithGitHub();
    }
  } catch (err) {
    console.error('Authentication process failed:', err.message);
    // Optional: Pass error state messages back to the UI layout here
  }
};

const closedModal = async () => {
  registrationModal.value = false;
};

const submitRegistration = async (userInfo) => {
  registrationModal.value = false;
  confirm(`Thank you for your request. Viasat will contact you soon to follow up.` +
    ` (Request ID: ${userInfo.requestId})`
  );
};

const loginWithGoogle = () => {
  const checkGoogleApi = setInterval(() => {
    /* global google */
    if (window.google?.accounts?.id) {
      clearInterval(checkGoogleApi);
      console.debug(`Google Identity Services ready`);
      
      google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          const idToken = response.credential;
          try {
          // Send token to backend for verification and JWT issuance
            const res = await fetch(ApiEndpoint.TOKEN, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                provider: 'google',
                token: idToken,
              }),
            });
            const authInfo = await res.json();
            if (!res.ok || !authInfo.token) {
              throw new Error(`Failed to obtain token`);
            }
            auth.login(authInfo);
            router.push({ name: defaultPage });
          } catch (err) {
            console.error(`Error during Google auth exchange: ${err.message}`);
          }
        },
      });
      
      // Render the Google button into the placeholder div
      google.accounts.id.renderButton(
        document.getElementById('googleBtn'),
        { theme: 'outline', size: 'large', width: '250' }
      );
      return;
    }
    console.warn(`Waiting for Google Identity Services script...`);
  }, 300);
};

const loginWithGitHub = async() => {
  // Callback logic is handled in LoginGithubCallbackView
  const redirectUri = `${window.location.origin}/github-callback`;
  const scope = 'user:email';

  // PKCE generation and storage
  const { verifier, challenge } = await setPKCE();
  
  const url = `https://github.com/login/oauth/authorize` +
      `?client_id=${encodeURIComponent(GITHUB_CLIENT_ID)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(scope)}` +
      `&response_type=code` +
      `&code_challenge=${challenge}` +
      `&code_challenge_method=S256`;
  
  window.location.href = url;
}

onMounted(() => {
  if (!isFirebase) loginWithGoogle();
});

</script>

<style scoped>
/* Minor brand styling tweaks extending Bootstrap utility defaults */
.btn-brand {
  transition: transform 0.1s ease, box-shadow 0.1s ease;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px;
}
.btn-brand:active {
  transform: scale(0.98);
}
.btn-white {
  background-color: #ffffff;
}
.btn-white:hover {
  background-color: #f8f9fa;
}
.btn-github {
  background-color: #24292e;
  border-color: #24292e;
}
.btn-github:hover {
  background-color: #1a1e22;
  border-color: #1a1e22;
}
</style>
